import { Box, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type { FieldStatusDto, SourcingEventV2Dto } from "../../../../../../autogen/bff-api";
import { TipTap, isTiptapContentEmpty } from "../../../../../../common/input/TipTap/TipTap";
import type {
  ReadinessLookup,
  KnownFieldKey,
} from "../../hooks/useReadinessByField";
import type { PublicEventStep } from "../../wizardSteps";
import {
  allowsNegotiation,
  isDpsScheme,
  isQualificationScheme,
  isTwoStageProcedure,
  offersAwardOnInitialTenders,
} from "../../utils/procedure";
import { requiresProcedureJustification } from "../../utils/publicProcurementOptions";
import { useDisplayDate } from "../../../../../../common/formatting/useDisplayDate";
import { EmptyValue, SummaryField } from "./SummaryField";
import { SummaryFieldGroup } from "./SummaryFieldGroup";
import { SummarySection } from "./SummarySection";

export const FIELD_KEYS = [
  "ProcedureTypeSet",
  "ProcedureSupportedByProfile",
  "ProcedureRequiresEeaTier",
  "CandidateMinimumAboveFloor",
  "CandidateMaximumNotBelowMinimum",
  "CandidateSelectionCriteriaPresent",
  "ProcedureJustificationPresent",
  "DpsUsageSet",
  "DpsValidityPresent",
  "DpsValidityOrdered",
  "QualificationSchemeValidityPresent",
  "QualificationSchemeValidityOrdered",
  "ProcedureRequiresUtilitiesRegulation",
  "TenderValidityMonthsSet",
  "SubmissionLanguagesSet",
  "SubmissionLanguagesValid",
] as const satisfies readonly KnownFieldKey[];

const LANGUAGE_LABELS: Record<string, string> = {
  NOR: "Norwegian",
  ENG: "English",
};

interface ProcedureSectionProps {
  event: SourcingEventV2Dto;
  readinessFor: ReadinessLookup;
  onWizardStepNavigate: (step: PublicEventStep) => void;
}

export const ProcedureSection = ({
  event,
  readinessFor,
  onWizardStepNavigate,
}: ProcedureSectionProps) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const publicContext = event.eventContext.type === "Public" ? event.eventContext : null;
  const candidateLimits = publicContext?.candidateLimits;
  const validityMonths = publicContext?.eFormsNoticeData?.tenderValidityMonths;
  const submissionLanguages = publicContext?.eFormsNoticeData?.submissionLanguages ?? [];

  const fieldStatuses = FIELD_KEYS.map(readinessFor).filter(
    (s): s is FieldStatusDto => s !== undefined,
  );

  return (
    <SummarySection
      title={t("Procedure")}
      editTarget={{ kind: "wizard-step", step: "procedure" }}
      fieldStatuses={fieldStatuses}
      onWizardStepNavigate={onWizardStepNavigate}
    >
      <SummaryFieldGroup>
        <SummaryField
          label={t("Procedure type")}
          value={
            publicContext?.procedureType
              ? t(publicContext.procedureType)
              : <EmptyValue text={t("Not set")} />
          }
          span="half"
          status={readinessFor("ProcedureTypeSet")}
          extraStatuses={[
            readinessFor("ProcedureSupportedByProfile"),
            readinessFor("ProcedureRequiresEeaTier"),
          ]}
        />
        {isTwoStageProcedure(publicContext?.procedureType) && (
          <SummaryField
            label={t("Limit on invited suppliers")}
            value={
              candidateLimits ? (
                <Box>
                  <Text>
                    {`${candidateLimits.minCandidates}${
                      candidateLimits.maxCandidates != null
                        ? `–${candidateLimits.maxCandidates}`
                        : "+"
                    }`}
                  </Text>
                  {isTiptapContentEmpty(candidateLimits.selectionCriteria) ? (
                    <EmptyValue text={t("Not set")} />
                  ) : (
                    <Box mt="1" fontSize="sm" color="smMuted">
                      <TipTap
                        content={candidateLimits.selectionCriteria}
                        editable={false}
                        hasBorder={false}
                        padding="0"
                        maxHeight=""
                      />
                    </Box>
                  )}
                </Box>
              ) : (
                t("All qualified suppliers are invited")
              )
            }
            span="half"
            status={readinessFor("CandidateMinimumAboveFloor")}
            extraStatuses={[
              readinessFor("CandidateMaximumNotBelowMinimum"),
              readinessFor("CandidateSelectionCriteriaPresent"),
            ]}
          />
        )}
        {allowsNegotiation(publicContext?.procedureType) &&
          requiresProcedureJustification(publicContext?.regulation) && (
          <SummaryField
            label={t("Grounds for negotiation (FOA § 13-2)")}
            value={
              publicContext?.procedureJustification &&
              !isTiptapContentEmpty(publicContext.procedureJustification.rationale) ? (
                <Box>
                  <Text>{t(publicContext.procedureJustification.ground)}</Text>
                  <Box mt="1" fontSize="sm" color="smMuted">
                    <TipTap
                      content={publicContext.procedureJustification.rationale}
                      editable={false}
                      hasBorder={false}
                      padding="0"
                      maxHeight=""
                    />
                  </Box>
                  {publicContext.noNegotiationNecessary === true &&
                    offersAwardOnInitialTenders(publicContext.procedureType) && (
                    <Text mt="1" fontSize="sm" color="smMuted">
                      {t("May be awarded on the initial tenders without negotiation")}
                    </Text>
                  )}
                </Box>
              ) : (
                <EmptyValue text={t("Not set")} />
              )
            }
            span="half"
            status={readinessFor("ProcedureJustificationPresent")}
          />
        )}
        {isDpsScheme(publicContext?.procedureType) && (
          <SummaryField
            label={t("The dynamic purchasing system")}
            value={
              publicContext?.dpsScheme ? (
                <Box>
                  <Text>
                    {`${displayDate(publicContext.dpsScheme.validFrom)} – ${displayDate(
                      publicContext.dpsScheme.validTo,
                    )}`}
                  </Text>
                  <Text mt="1" fontSize="sm" color="smMuted">
                    {publicContext.dynamicPurchasingSystem
                      ? t(publicContext.dynamicPurchasingSystem)
                      : t("Not set")}
                  </Text>
                </Box>
              ) : (
                <EmptyValue text={t("Not set")} />
              )
            }
            span="half"
            status={readinessFor("DpsValidityPresent")}
            extraStatuses={[readinessFor("DpsValidityOrdered"), readinessFor("DpsUsageSet")]}
          />
        )}
        {isQualificationScheme(publicContext?.procedureType) && (
          <SummaryField
            label={t("The qualification system")}
            value={
              publicContext?.qualificationScheme ? (
                <Text>
                  {`${displayDate(publicContext.qualificationScheme.validFrom)} – ${displayDate(
                    publicContext.qualificationScheme.validTo,
                  )}`}
                </Text>
              ) : (
                <EmptyValue text={t("Not set")} />
              )
            }
            span="half"
            status={readinessFor("QualificationSchemeValidityPresent")}
            extraStatuses={[
              readinessFor("QualificationSchemeValidityOrdered"),
              readinessFor("ProcedureRequiresUtilitiesRegulation"),
            ]}
          />
        )}
        <SummaryField
          label={t("Tender validity period")}
          value={
            isTwoStageProcedure(publicContext?.procedureType)
              ? t("Set when the invitation to tender is sent")
              : validityMonths != null
                ? `${validityMonths} ${t("month", { count: validityMonths })}`
                : <EmptyValue text={t("Not set")} />
          }
          span="half"
          status={readinessFor("TenderValidityMonthsSet")}
        />
        <SummaryField
          label={t("Submission language")}
          value={
            submissionLanguages.length > 0
              ? submissionLanguages.map((code) => t(LANGUAGE_LABELS[code] ?? code)).join(", ")
              : <EmptyValue text={t("Not set")} />
          }
          span="half"
          status={readinessFor("SubmissionLanguagesSet")}
          extraStatuses={[readinessFor("SubmissionLanguagesValid")]}
        />
      </SummaryFieldGroup>
    </SummarySection>
  );
};
